import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { isNil, isEqual } from 'lodash' // Use isEqual to compare lists
import { Grid, Typography, Box, useTheme, useMediaQuery } from '@mui/material'
import { List } from 'components/Listing/List'
import Loader from 'components/Loader'
import { Filters } from '../../components/Filters'
import { FilterType } from '../../components/Filters/filterDefinitions'
import { fetchAllCandidates } from 'src/redux/candidate'
import { applyFilters } from 'components/Filters/helpers'
import PhysicianContent from './PhysicianContent'
import { fetchProviderFilterValues } from 'src/redux/filters'
import { addSystemMessage, setActiveSearchSource } from 'src/redux/aiCandidateSearch'
import AICandidateChatBot from 'pages/Hospitals/Candidates/AI/AICandidateChatBot'
import { mapCandidateListingArgumentsToFilterParams, buildFilterChangeMessage } from 'pages/Hospitals/Candidates/AI/candidateFilterSyncHelpers'

const Physicians = () => {
  const { signedIn, employerId, employerUserId } = useSelector(state => state.identity)
  const dispatch = useDispatch()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const [activeItem, setActiveItem] = useState(null)
  const [page, setPage] = useState(1)
  const pageSize = 50
  const physicianList = useSelector(state => state.candidate.list) || []
  const { isLoadingFilters } = useSelector(state => state.filters).loading
  const filterValues = useSelector(state => state.filters.providerFilterValues) || {}
  const totalCandidates = useSelector(state => state.candidate.totalCandidates)
  const totalPages = Math.ceil(totalCandidates / pageSize)
  const { loading: isLoadingCandidates } = useSelector(state => state.candidate)
  const { rankedCandidates, candidateListingArguments: aiCandidateListingArguments, resolvedFilters: aiResolvedFilters, conversationId, activeSearchSource } = useSelector(state => state.aiCandidateSearch)
  const [searchParams, setSearchParams] = useSearchParams()
  const [displayedData, setDisplayedData] = useState([]) // Start with an empty array
  const candidateTypeId = searchParams.get('candidateTypeId')

  const isAIMode = activeSearchSource === 'ai'
  const aiCandidateList = useMemo(
    () => (rankedCandidates || [])
      .filter(rc => rc.candidate)
      .map(rc => ({
        ...rc.candidate,
        matchTier: rc.matchTier,
        justification: rc.justification,
        chatbotMessageId: rc.chatbotMessageId,
        conversationId,
        feedback: rc.feedback,
        firstName: rc.candidate.user?.firstName,
        lastName: rc.candidate.user?.lastName,
        city: rc.candidate.user?.city,
        state: rc.candidate.user?.state,
        profileImage: rc.candidate.user?.profileImage,
        listingTitle: `${rc.candidate.user?.firstName || ''} ${rc.candidate.user?.lastName || ''}`.trim(),
        listingSubLabel: rc.candidate.specialties?.length > 0
          ? `${rc.candidate.specialties[0].name}${rc.candidate.subspecialties?.length > 0 ? ' | ' + rc.candidate.subspecialties[0].name : ''}`
          : '',
        image: rc.candidate.user?.profileImage,
        phone: rc.candidate.user?.phone,
        email: rc.candidate.user?.email,
        showPhone: rc.candidate.user?.showPhone,
        showEmail: rc.candidate.user?.showEmail,
        createdAt: rc.candidate.user?.createdAt,
        lastLoginAt: rc.candidate.user?.lastLoginAt,
        emailVerifiedAt: rc.candidate.user?.emailVerifiedAt,
        candidateType: rc.candidate.candidateTypeInfo,
        specialties: rc.candidate.specialties || [],
        subspecialties: rc.candidate.subspecialties || [],
      })),
    [rankedCandidates],
  )

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('physiciansLeftPanelWidth')
    // Default 30% of typical viewport width (assuming ~1400px width = ~420px)
    return saved ? parseInt(saved) : 420
  })
  const [isResizing, setIsResizing] = useState(false)

  const startResize = (e) => {
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return
    
    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('physicians-container')
        if (!container) return
        
        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left
        
        if (newWidth >= 250 && newWidth <= 900) {
          setLeftPanelWidth(newWidth)
        }
      })
    }
    
    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('physiciansLeftPanelWidth', leftPanelWidth.toString())
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, leftPanelWidth, mobile])

  const handleListItemClick = currId => {
    const activePhysician = !isNil(currId)
      ? displayedData.find(p => `${p.id}` === `${currId}`)
      : displayedData.length > 0
        ? displayedData[0]
        : null
    setActiveItem(activePhysician)
  }

  // Fetch provider filter values
  useEffect(() => {
    const specialtyIdsParam = searchParams.get('specialtyIds')
    const specialtyIds = specialtyIdsParam ? specialtyIdsParam.split('|') : []

    const filterValues = {}

    // Only add candidateTypeId if it exists
    if (candidateTypeId) {
      filterValues.candidateTypeId = candidateTypeId
    }

    // Only add specialtyIds if they exist
    if (specialtyIds.length > 0) {
      filterValues.specialtyIds = specialtyIds
    }
    dispatch(fetchProviderFilterValues(filterValues))
  }, [candidateTypeId, searchParams, dispatch])

  const effectiveList = isAIMode ? aiCandidateList : physicianList

  useEffect(() => {
    if (!isEqual(displayedData, effectiveList)) {
      setDisplayedData(effectiveList)
    }
  }, [effectiveList, displayedData])

  useEffect(() => {
    if (effectiveList.length > 0 && !mobile) {
      setActiveItem(effectiveList[0])
    } else if (mobile) {
      setActiveItem(null)
    } else if (effectiveList.length === 0) {
      setActiveItem(null)
    }
  }, [effectiveList, mobile])

  const leftPanelHeader = isAIMode
    ? `Stethi Results: ${aiCandidateList.length} Provider${aiCandidateList.length !== 1 ? 's' : ''}`
    : 'Results: ' + `${totalCandidates ? totalCandidates.toLocaleString() : 0} ` + 'Providers'

  // --- Filter <-> AI Sync ---
  const aiSyncGenerationRef = useRef(0)
  const filterSyncGenerationRef = useRef(0)
  const previousParamsRef = useRef(new URLSearchParams(searchParams))
  const filterTriggeredTransitionRef = useRef(false)

  // AI -> Filters: when AI returns results, update URL params to reflect search criteria
  useEffect(() => {
    if (activeSearchSource !== 'ai' || !aiCandidateListingArguments) return

    const aiParams = mapCandidateListingArgumentsToFilterParams(
      aiCandidateListingArguments,
      aiResolvedFilters,
      filterValues,
    )

    const currentCandidateTypeId = searchParams.get('candidateTypeId')
    if (currentCandidateTypeId && !aiParams.has('candidateTypeId')) {
      aiParams.set('candidateTypeId', currentCandidateTypeId)
    }

    aiSyncGenerationRef.current += 1
    previousParamsRef.current = new URLSearchParams(aiParams)
    setSearchParams(aiParams, { replace: true })
  }, [aiCandidateListingArguments, activeSearchSource])

  // Filters -> AI: when user changes filters, add system message and switch to traditional
  useEffect(() => {
    if (aiSyncGenerationRef.current !== filterSyncGenerationRef.current) {
      filterSyncGenerationRef.current = aiSyncGenerationRef.current
      return
    }

    if (!employerUserId) return

    const message = buildFilterChangeMessage(
      previousParamsRef.current,
      searchParams,
      filterValues,
    )

    const prevString = previousParamsRef.current.toString()
    const currString = searchParams.toString()
    if (!message && prevString === currString) return

    if (message) {
      dispatch(addSystemMessage(message))
    }

    previousParamsRef.current = new URLSearchParams(searchParams)

    if (conversationId && activeSearchSource === 'ai') {
      filterTriggeredTransitionRef.current = true
      dispatch(setActiveSearchSource('traditional'))
    }
  }, [searchParams, conversationId, activeSearchSource, filterValues, employerUserId, dispatch])

  // When switching from AI to traditional, handle filter cleanup
  const previousSearchSourceRef = useRef(activeSearchSource)
  useEffect(() => {
    if (previousSearchSourceRef.current === 'ai' && activeSearchSource === 'traditional') {
      aiSyncGenerationRef.current += 1
      if (!filterTriggeredTransitionRef.current) {
        setSearchParams(new URLSearchParams(), { replace: true })
      }
      filterTriggeredTransitionRef.current = false
    }
    previousSearchSourceRef.current = activeSearchSource
  }, [activeSearchSource, setSearchParams])

  const applyCandidateFilters = ({ searchParams }) => {
    applyFilters({
      searchParams,
      employerId,
      employerUserId,
      dispatch,
      getData: fetchAllCandidates,
      page,
      pageSize,
    })
  }

  // Trigger filter application when searchParams change
  useEffect(() => {
    applyCandidateFilters({ searchParams })
  }, [searchParams, page])

  return (
    <>
      <Grid container justifyContent='center'>
        {signedIn && Object.keys(filterValues).length > 0 && (
          <Grid item xs={12}>
            <Filters
              filterType={FilterType.CANDIDATE}
              filterValues={filterValues}
              listType={'provider'}
              loading={isLoadingFilters}
              applyFilters={applyCandidateFilters}
            />
          </Grid>
        )}
        {isLoadingCandidates ? (
          <Loader />
        ) : (
          <Box id="physicians-container" sx={{
            display: 'flex',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            px: { xs: 0, md: 4 },
          }}>
            <Grid container item sm={12} direction='row' sx={{
              height: '100%',
              flex: { xs: '1', md: `0 0 ${leftPanelWidth}px` },
              width: { xs: '100%', md: 'auto' },
              overflow: 'hidden',
              px: { xs: 0 },
            }}>
              <Grid item>
                <Typography variant='h6' sx={{ padding: '10px 16px' }}>
                  {leftPanelHeader}
                </Typography>
              </Grid>
              <Grid container alignItems='flex-start' sx={{ maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
                <List
                  setPage={setPage}
                  page={isAIMode ? 1 : page}
                  data={displayedData}
                  listType={'provider'}
                  isPhysicianList={true}
                  handleListItemClick={handleListItemClick}
                  dataTotal={isAIMode ? aiCandidateList.length : totalCandidates}
                  totalPages={isAIMode ? 1 : totalPages}
                  signedIn={signedIn}
                  isLoading={isLoadingFilters}
                  setActiveItem={setActiveItem}
                  setDisplayedData={setDisplayedData}
                />
              </Grid>
            </Grid>
            
            {/* Resizable Divider */}
            <Box
              onMouseDown={startResize}
              sx={{
                display: { xs: 'none', md: 'block' },
                flexShrink: 0,
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '1px',
                  height: '100%',
                  backgroundColor: 'divider',
                },
                '&:hover, &:active': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
            
            <Box display={{ xs: 'none', sm: 'none', md: 'contents' }}>
              <Grid
                container
                item
                direction='column'
                sx={{
                  maxHeight: 'calc(100vh - 64px)',
                  flex: '1 1 0%',
                  minWidth: 0,
                  width: { xs: '100%', md: 'auto' },
                  overflow: 'auto',
                  px: { xs: 0 },
                }}
              >
                {activeItem && <PhysicianContent item={activeItem} signedIn={signedIn} />}
              </Grid>
            </Box>
          </Box>
        )}
      </Grid>
      {employerUserId && <AICandidateChatBot />}
    </>
  )
}

export default Physicians
